import { tryToParseInt } from "@/components/utils/tryToParseInt";
import { useMultiFieldSet } from "@/components/utils/useFieldSet";
import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import { isNil } from "lodash";
import {
  JobAppQuestionAnswerRequest,
  JobAppQuestionAnswerSelectionRequest,
  JobAppSectionResponseSectionType,
} from "modules/api-docs";
import {
  SelfApplyCustomQuestionAnswer,
  customQuestionsYupSchema,
} from "modules/self-apply/validation/candidate-self-apply";
import { useMemo } from "react";
import {
  SelfApplyOnlyBaseJobAppSectionFieldSetArgs,
  SelfApplyOnlyNullish,
  SelfApplyOnlyNullishExistingJobApp,
  SelfApplyOnlyNullishJobSection,
} from "./common";

type CustomQuestionSection = SelfApplyOnlyNullishJobSection & {
  jobQuestions?: SelfApplyOnlyNullish<
    SelfApplyOnlyNullish<{
      orgQuestionId?: SelfApplyOnlyNullish<number>;
      isRequired?: SelfApplyOnlyNullish<boolean>;
      questionType?: SelfApplyOnlyNullish<string>;
    }>[]
  >;
};

type ExistingCustomQuestionAnswer = NonNullable<
  NonNullable<SelfApplyOnlyNullishExistingJobApp>["answers"]
>[number];

function getExistingAnswerByCustomQuestionId(
  existingAnswers: ExistingCustomQuestionAnswer[],
) {
  const existingAnswerIdByQuestionId = new Map<
    number,
    ExistingCustomQuestionAnswer
  >();
  if (!existingAnswers) {
    return new Map<number, ExistingCustomQuestionAnswer>();
  }

  for (const existingAnswer of existingAnswers) {
    if (!existingAnswer) {
      continue;
    }
    const questionId = existingAnswer.orgQuestionId;
    if (isNil(questionId)) {
      continue;
    }

    existingAnswerIdByQuestionId.set(questionId, existingAnswer);
  }

  return existingAnswerIdByQuestionId;
}

export function extractCustomQuestionAnswersFromExistingJobApp(args: {
  jobSections: CustomQuestionSection[];
  existingAnswers: ExistingCustomQuestionAnswer[];
}) {
  const existingAnswers: SelfApplyCustomQuestionAnswer[] = [];
  if (!args.jobSections) {
    return existingAnswers;
  }

  const customQuestions = args.jobSections
    .filter(NonNullish)
    .filter(
      (x) =>
        x.jobSectionType === JobAppSectionResponseSectionType.CUSTOM_QUESTIONS,
    )
    .flatMap((x) => x.jobQuestions)
    .filter(NonNullish);

  const existingAnswerByQuestionId = getExistingAnswerByCustomQuestionId(
    args.existingAnswers,
  );

  for (const question of customQuestions) {
    const questionId = question.orgQuestionId;

    // Invalid case; answer should always have a question
    if (isNil(questionId)) {
      continue;
    }

    // Invalid case: need question type to render the question
    if (!question.questionType) {
      continue;
    }

    const existingAnswer = existingAnswerByQuestionId.get(questionId);
    const existingAnswerId = tryToParseInt(existingAnswer?.id);
    // Populate with empty answer for unanswered questions
    if (
      // Question not answered
      !existingAnswer ||
      // Something went wrong; question was already answered but no answer id from the backend
      !existingAnswerId.isJust()
    ) {
      existingAnswers.push({
        questionType: question.questionType,
        isRequired: !!question.isRequired,
        dateValue: undefined,
        id: undefined,
        jobAppQuestionAnswerSelections: [],
        numericValue: undefined,
        orgQuestionId: questionId,
        textValue: "",
      });

      continue;
    }

    // E.g. if question is single or multi-select
    const existingAnswerSelections =
      existingAnswer?.jobAppQuestionAnswerSelections?.edges
        ?.map((edge) => edge?.node)
        ?.filter(NonNullish)
        ?.map((node) => {
          const selectionReq: JobAppQuestionAnswerSelectionRequest = {
            jobAppQuestionAnswerId: node.jobAppQuestionAnswerId ?? undefined,
            orgQuestionOptionId: node.orgQuestionOptionId ?? undefined,
            id: tryToParseInt(node.id).extract(),
          };

          return selectionReq;
        }) ?? [];

    const answerRequest: SelfApplyCustomQuestionAnswer = {
      questionType: question.questionType,
      isRequired: !!question.isRequired,
      dateValue: existingAnswer?.dateValue ?? undefined,
      id: existingAnswerId.extract(),
      jobAppQuestionAnswerSelections: existingAnswerSelections,
      numericValue: existingAnswer?.numericValue ?? undefined,
      orgQuestionId: questionId,
      textValue: existingAnswer?.textValue ?? "",
    };

    existingAnswers.push(answerRequest);
  }

  return existingAnswers;
}

type UseCustomQuestionAnswerFieldSetArgs =
  SelfApplyOnlyBaseJobAppSectionFieldSetArgs & {
    jobSections?: SelfApplyOnlyNullish<CustomQuestionSection[]>;
  };
export function useCustomQuestionAnswersFieldSet({
  jobSections,
  existingJobApp,
}: UseCustomQuestionAnswerFieldSetArgs) {
  const existingAnswers = existingJobApp?.answers;

  const initialValue = useMemo(() => {
    return extractCustomQuestionAnswersFromExistingJobApp({
      jobSections,
      existingAnswers: existingAnswers ?? [],
    });
  }, [jobSections, existingAnswers]);

  return useMultiFieldSet<JobAppQuestionAnswerRequest>(
    initialValue,
    [initialValue],
    customQuestionsYupSchema,
  );
}

/**
 * When updating an existing question/answer, sets the answer id in the request.
 */
export function customQuestionAnswersToRequests(args: {
  answers: JobAppQuestionAnswerRequest[];
  existingAnswers: ExistingCustomQuestionAnswer[];
}): JobAppQuestionAnswerRequest[] {
  // All new, so ids to set
  if (!args.existingAnswers || args.existingAnswers.length === 0) {
    return args.answers;
  }

  const existingAnswerByQuestionId = getExistingAnswerByCustomQuestionId(
    args.existingAnswers,
  );

  return (
    args.answers
      // Apply any existing answer ids to the question/answer
      .map((answer) => {
        const questionId = answer.orgQuestionId;
        // Shouldn't submit an answer without a question id
        if (isNil(questionId)) {
          return null;
        }

        const request: JobAppQuestionAnswerRequest = {
          dateValue: answer.dateValue,
          textValue: answer.textValue,
          jobAppId: answer.jobAppId,
          jobAppQuestionAnswerSelections: answer.jobAppQuestionAnswerSelections,
          numericValue: answer.numericValue,
          orgQuestionId: answer.orgQuestionId,
          id: tryToParseInt(
            existingAnswerByQuestionId.get(questionId)?.id,
          ).extract(),
        };

        return request;
      })
      .filter(NonNullish)
  );
}
